import React, { useEffect, useState } from 'react';
import { Loader2, Save, Trash2 } from 'lucide-react';
import { Button } from '../Button';
import {
    ProjectOrderValue,
    deleteOrderValue,
    saveOrderValue,
} from '../../services/erpSteeringService';
import { auftragswertCentsAusEingabe, centsAlsEingabe } from '../../services/erpSteering';
import { formatCents } from '../../services/freelancerLogic';
import { rechtFehlerOder } from '../../services/rechte/rechtFehler';
import { KartenRahmen } from './KartenRahmen';

// Pflege der fixierten Auftragswerte (project_order_values).
//
// Angelegt werden sie im Regelfall bei der Angebotsannahme
// (AcceptQuoteDialog → crmService.acceptQuote). Hier stehen sie zum
// Nachtragen und Korrigieren — Nachtraege erhoehen den Auftrag, ohne dass ein
// neues Angebot entsteht, und Bestandsprojekte haben nie eines gehabt.
//
// Bewusst KEIN Projekt-Auswahldialog fuer neue Zeilen: die Projekt-ID kaeme
// aus einer weiteren Liste ueber alle Projekte, und der Weg dorthin ist der
// Auftragsbestand, der die Luecke ohnehin ausweist. Was hier fehlt, gehoert in
// die Auftragsbestandstabelle — dort steht das Projekt schon.

interface Props {
    zustand: { daten: ProjectOrderValue[] | null; laedt: boolean; fehler: string | null };
    onGeaendert: () => void;
}

const inputClass =
    'px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all';

export const AuftragswerteCard: React.FC<Props> = ({ zustand, onGeaendert }) => {
    const [entwuerfe, setEntwuerfe] = useState<Record<string, string>>({});
    const [notizen, setNotizen] = useState<Record<string, string>>({});
    const [speichertId, setSpeichertId] = useState<string | null>(null);
    const [fehler, setFehler] = useState<string | null>(null);

    // Entwuerfe an die geladenen Zeilen angleichen: nach einem Neuladen sollen
    // die Felder zeigen, was in der Datenbank steht, nicht was vorher im
    // Formular stand.
    useEffect(() => {
        if (!zustand.daten) return;
        setEntwuerfe(
            Object.fromEntries(zustand.daten.map((z) => [z.id, centsAlsEingabe(z.orderValueCents)])),
        );
        setNotizen(Object.fromEntries(zustand.daten.map((z) => [z.id, z.note || ''])));
    }, [zustand.daten]);

    const speichern = async (zeile: ProjectOrderValue) => {
        const cents = auftragswertCentsAusEingabe(entwuerfe[zeile.id] ?? '');
        if (cents === null) {
            setFehler('Bitte einen gültigen Betrag eingeben (z. B. 12.345,67).');
            return;
        }
        setSpeichertId(zeile.id);
        setFehler(null);
        try {
            await saveOrderValue({
                projectId: zeile.projectId,
                orderValueCents: cents,
                quoteId: zeile.quoteId,
                note: notizen[zeile.id],
            });
            onGeaendert();
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        } finally {
            setSpeichertId(null);
        }
    };

    const entfernen = async (zeile: ProjectOrderValue) => {
        setSpeichertId(zeile.id);
        setFehler(null);
        try {
            await deleteOrderValue(zeile.projectId);
            onGeaendert();
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        } finally {
            setSpeichertId(null);
        }
    };

    return (
        <div className="space-y-4">
            <p className="text-xs text-slate-500">
                Der fixierte Auftragswert je Projekt. Er wird bei der Angebotsannahme vorgeschlagen und folgt
                späteren Angebotsänderungen bewusst nicht — Nachträge werden hier eingetragen.
            </p>
            {fehler && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{fehler}</div>
            )}

            <KartenRahmen
                laedt={zustand.laedt}
                fehler={zustand.fehler}
                leer={!!zustand.daten && zustand.daten.length === 0}
                leerText="Noch kein Auftragswert fixiert."
            >
                {zustand.daten && (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-slate-200 text-left text-[11px] uppercase tracking-widest text-slate-500">
                                    <th className="py-2 pr-3 font-bold">Projekt-ID</th>
                                    <th className="py-2 pr-3 font-bold">Auftragswert (EUR netto)</th>
                                    <th className="py-2 pr-3 font-bold">Notiz</th>
                                    <th className="py-2 pr-3 font-bold">Fixiert am</th>
                                    <th className="py-2 font-bold" />
                                </tr>
                            </thead>
                            <tbody>
                                {zustand.daten.map((z) => (
                                    <tr key={z.id} className="border-b border-slate-100">
                                        <td className="py-2 pr-3">
                                            <span className="font-mono text-xs text-slate-500">{z.projectId.slice(0, 8)}…</span>
                                            <p className="text-xs text-slate-400">aktuell {formatCents(z.orderValueCents)}</p>
                                        </td>
                                        <td className="py-2 pr-3">
                                            <input
                                                className={`${inputClass} w-36 text-right tabular-nums`}
                                                inputMode="decimal"
                                                aria-label="Auftragswert"
                                                value={entwuerfe[z.id] ?? ''}
                                                onChange={(e) =>
                                                    setEntwuerfe((prev) => ({ ...prev, [z.id]: e.target.value }))
                                                }
                                            />
                                        </td>
                                        <td className="py-2 pr-3">
                                            <input
                                                className={`${inputClass} w-full min-w-[12rem]`}
                                                aria-label="Notiz"
                                                value={notizen[z.id] ?? ''}
                                                onChange={(e) =>
                                                    setNotizen((prev) => ({ ...prev, [z.id]: e.target.value }))
                                                }
                                            />
                                        </td>
                                        <td className="py-2 pr-3 text-xs text-slate-500">
                                            {z.fixedAt ? new Date(z.fixedAt).toLocaleDateString('de-DE') : '—'}
                                        </td>
                                        <td className="py-2">
                                            <div className="flex items-center gap-2">
                                                <Button
                                                    variant="secondary"
                                                    className="h-9 text-xs"
                                                    disabled={speichertId === z.id}
                                                    onClick={() => void speichern(z)}
                                                    icon={
                                                        speichertId === z.id ? (
                                                            <Loader2 className="h-4 w-4 animate-spin" />
                                                        ) : (
                                                            <Save className="h-4 w-4" />
                                                        )
                                                    }
                                                >
                                                    Speichern
                                                </Button>
                                                <button
                                                    type="button"
                                                    title="Auftragswert entfernen"
                                                    className="rounded-lg p-2 text-slate-400 transition-all hover:bg-red-50 hover:text-red-600"
                                                    disabled={speichertId === z.id}
                                                    onClick={() => void entfernen(z)}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </KartenRahmen>
        </div>
    );
};
